import { useRef, useEffect, useState } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const CameraController = ({ 
  isMirrorActive, 
  onApproachMirror,
  enabled = true 
}) => {
  const { camera } = useThree();
  const keysPressed = useRef({});
  const isDragging = useRef(false);
  const previousMousePosition = useRef({ x: 0, y: 0 });
  const [isMobile] = useState(() => 
    typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
  
  // Movement with inertia
  const velocity = useRef(new THREE.Vector3(0, 0, 0));
  const rotationVelocity = useRef({ x: 0, y: 0 });
  
  // Store the camera's yaw and pitch
  const cameraYaw = useRef(0);
  const cameraPitch = useRef(0);
  
  // Movement settings
  const baseMoveSpeed = 0.06;
  const sprintMultiplier = 1.8;
  const rotationSpeed = 0.002;
  const friction = 0.88;
  const rotationFriction = 0.85;
  
  // Room boundaries (collision with walls)
  const bounds = {
    minX: -4, maxX: 4,
    minZ: -2.5, maxZ: 4,
    minY: 1.2, maxY: 2.2
  };
  
  // Furniture collision boxes
  const collisionBoxes = [
    // Vanity table
    { min: new THREE.Vector3(-1.3, 0, -2.5), max: new THREE.Vector3(1.3, 1, -1.5) },
    // Left shelf area
    { min: new THREE.Vector3(-2.8, 0, -3), max: new THREE.Vector3(-1.5, 2, -2.7) },
    // Right shelf area
    { min: new THREE.Vector3(1.5, 0, -3), max: new THREE.Vector3(2.8, 2, -2.7) },
  ];

  // Check collision with furniture
  const checkCollision = (position) => {
    const playerRadius = 0.3;
    const playerBox = new THREE.Box3(
      new THREE.Vector3(position.x - playerRadius, position.y - 0.5, position.z - playerRadius),
      new THREE.Vector3(position.x + playerRadius, position.y + 0.5, position.z + playerRadius)
    );
    
    for (const box of collisionBoxes) {
      const furnitureBox = new THREE.Box3(box.min, box.max);
      if (playerBox.intersectsBox(furnitureBox)) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    // Position camera to face the vanity and mirror
    camera.position.set(0, 1.6, 3);
    cameraYaw.current = 0;
    cameraPitch.current = 0;
    camera.rotation.order = 'YXZ';
    camera.rotation.y = cameraYaw.current;
    camera.rotation.x = cameraPitch.current;
    
    const handleKeyDown = (e) => {
      keysPressed.current[e.key.toLowerCase()] = true;
      if (e.key.toLowerCase() === 'e') {
        const distanceToMirror = camera.position.distanceTo(new THREE.Vector3(0, 1.6, -2));
        if (distanceToMirror < 4) {
          onApproachMirror?.();
        }
      }
    };
    
    const handleKeyUp = (e) => {
      keysPressed.current[e.key.toLowerCase()] = false;
    };

    const handleMouseDown = (e) => {
      if (e.button === 0) {
        isDragging.current = true;
        previousMousePosition.current = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current || isMirrorActive) return;
      
      const deltaX = e.clientX - previousMousePosition.current.x;
      const deltaY = e.clientY - previousMousePosition.current.y;
      
      rotationVelocity.current.y -= deltaX * rotationSpeed;
      rotationVelocity.current.x -= deltaY * rotationSpeed * 0.5;
      
      previousMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    // Touch controls for mobile
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        isDragging.current = true;
        previousMousePosition.current = { 
          x: e.touches[0].clientX, 
          y: e.touches[0].clientY 
        };
      }
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current || isMirrorActive || e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      const deltaX = touch.clientX - previousMousePosition.current.x;
      const deltaY = touch.clientY - previousMousePosition.current.y;
      
      rotationVelocity.current.y -= deltaX * rotationSpeed * 1.5;
      rotationVelocity.current.x -= deltaY * rotationSpeed * 0.8;
      
      previousMousePosition.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = () => {
      isDragging.current = false;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [camera, onApproachMirror, isMirrorActive]);

  useFrame(() => {
    if (!enabled || isMirrorActive) {
      velocity.current.multiplyScalar(friction);
      rotationVelocity.current.x *= rotationFriction;
      rotationVelocity.current.y *= rotationFriction;
      return;
    }
    
    const keys = keysPressed.current;
    const isSprinting = keys['shift'];
    const currentMoveSpeed = baseMoveSpeed * (isSprinting ? sprintMultiplier : 1);
    
    // Update yaw and pitch from rotation velocity
    cameraYaw.current += rotationVelocity.current.y;
    cameraPitch.current = THREE.MathUtils.clamp(
      cameraPitch.current + rotationVelocity.current.x,
      -Math.PI / 4,
      Math.PI / 4
    );
    
    // Apply rotation friction
    rotationVelocity.current.x *= rotationFriction;
    rotationVelocity.current.y *= rotationFriction;
    
    // Calculate forward direction from yaw (horizontal only)
    const forward = new THREE.Vector3(
      -Math.sin(cameraYaw.current),
      0,
      -Math.cos(cameraYaw.current)
    );
    
    // Calculate right direction
    const right = new THREE.Vector3(
      Math.cos(cameraYaw.current),
      0,
      -Math.sin(cameraYaw.current)
    );
    
    // Calculate acceleration based on keys
    const acceleration = new THREE.Vector3(0, 0, 0);
    
    if (keys['w'] || keys['arrowup']) {
      acceleration.addScaledVector(forward, currentMoveSpeed);
    }
    if (keys['s'] || keys['arrowdown']) {
      acceleration.addScaledVector(forward, -currentMoveSpeed);
    }
    if (keys['a'] || keys['arrowleft']) {
      acceleration.addScaledVector(right, -currentMoveSpeed);
    }
    if (keys['d'] || keys['arrowright']) {
      acceleration.addScaledVector(right, currentMoveSpeed);
    }
    
    // Add acceleration to velocity
    velocity.current.add(acceleration);
    
    // Apply friction
    velocity.current.multiplyScalar(friction);
    
    // Calculate new position
    const newPosition = camera.position.clone().add(velocity.current);
    
    // Check collision with furniture
    if (!checkCollision(newPosition)) {
      camera.position.copy(newPosition);
    } else {
      // Slide along surfaces - try moving only in X or Z
      const xOnlyPosition = new THREE.Vector3(newPosition.x, camera.position.y, camera.position.z);
      const zOnlyPosition = new THREE.Vector3(camera.position.x, camera.position.y, newPosition.z);
      
      if (!checkCollision(xOnlyPosition)) {
        camera.position.x = newPosition.x;
      }
      if (!checkCollision(zOnlyPosition)) {
        camera.position.z = newPosition.z;
      }
      
      // Reduce velocity on collision
      velocity.current.multiplyScalar(0.5);
    }
    
    // Apply rotation
    camera.rotation.order = 'YXZ';
    camera.rotation.y = cameraYaw.current;
    camera.rotation.x = cameraPitch.current;
    
    // Clamp position within room bounds
    const softBounce = 0.3;
    
    if (camera.position.x < bounds.minX) {
      camera.position.x = bounds.minX;
      velocity.current.x *= -softBounce;
    } else if (camera.position.x > bounds.maxX) {
      camera.position.x = bounds.maxX;
      velocity.current.x *= -softBounce;
    }
    
    if (camera.position.y < bounds.minY) {
      camera.position.y = bounds.minY;
      velocity.current.y *= -softBounce;
    } else if (camera.position.y > bounds.maxY) {
      camera.position.y = bounds.maxY;
      velocity.current.y *= -softBounce;
    }
    
    if (camera.position.z < bounds.minZ) {
      camera.position.z = bounds.minZ;
      velocity.current.z *= -softBounce;
    } else if (camera.position.z > bounds.maxZ) {
      camera.position.z = bounds.maxZ;
      velocity.current.z *= -softBounce;
    }
  });

  return null;
};

export default CameraController;
